import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CALL_PERIODS } from '@ultrasnip/contracts';
import { useCallback } from 'react';
import { z } from 'zod';
import { PageHeader } from '@/components/shell/page-header';
import { AnalyticsScreen, type AnalyticsView } from '@/features/calls/analytics-screen';
import { callersQuery, callsQuery } from '@/features/calls/calls-api';

export const Route = createFileRoute('/_app/analytics')({
  staticData: { fullWidth: true },
  validateSearch: z.object({
    /** Only this caller's calls. */
    caller: z
      .string()
      .regex(/^[a-f0-9]{24}$/)
      .optional()
      .catch(undefined),
    period: z.enum(CALL_PERIODS).optional().catch(undefined),
    q: z.string().max(64).optional().catch(undefined),
    /** Timeline; grouped by caller is the default and stays out of the URL. */
    view: z.enum(['timeline']).optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context: { queryClient }, deps }) => {
    void queryClient.prefetchQuery(callersQuery);
    void queryClient.prefetchInfiniteQuery(
      callsQuery({ callerId: deps.caller, period: deps.period ?? 'all', q: deps.q ?? '' }),
    );
  },
  component: AnalyticsRoute,
});

function AnalyticsRoute() {
  const { caller, period = 'all', q = '', view } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const onChange = useCallback(
    (
      next: Partial<{
        callerId: string | undefined;
        period: (typeof CALL_PERIODS)[number];
        q: string;
        view: AnalyticsView;
      }>,
    ) =>
      void navigate({
        search: (prev) => ({
          ...prev,
          ...('callerId' in next ? { caller: next.callerId } : {}),
          ...(next.period !== undefined ? { period: next.period === 'all' ? undefined : next.period } : {}),
          ...(next.q !== undefined ? { q: next.q || undefined } : {}),
          ...(next.view !== undefined
            ? { view: next.view === 'timeline' ? ('timeline' as const) : undefined }
            : {}),
        }),
        // Typing a search refines the view in place; other filters are steps Back can undo.
        replace: next.q !== undefined,
      }),
    [navigate],
  );
  return (
    <>
      <PageHeader title="Analytics" />
      <AnalyticsScreen callerId={caller} period={period} q={q} view={view ?? 'callers'} onChange={onChange} />
    </>
  );
}
